import { useLookbookContext } from '@app/components/lookbook/context/LookbookContext';
import { useLookbookDetailsModalContext } from '@app/components/lookbook/context/LookbookDetailsModalContext';
import { Button } from '@app/components/various/Button';
import { FocusTrap } from '@app/components/various/FocusTrap';
import { CancelSmall } from '@app/components/various/Icons';
import { Portal } from '@app/components/various/Portal';
import { clearLookbookProductSearchQuery, getUrlForLookbookProduct, ProductUrlProps } from '@app/logic/products';
import { useLookbookProduct } from '@app/services/hooks/lookbook/useLookbookProduct';
import { GAProductListItemId, GAProductListItemName, mapProductsToGAItems, useGTM } from '@app/utils/analytics';
import { handleEscapeKey } from '@app/utils/handleEscapeKey';
import { useAmplitude } from '@app/utils/hooks';
import { useBodyLock } from '@app/utils/hooks/useBodyLock';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Id } from '@typings';
import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import { ProductDetailsLayoutContextProvider } from '../context/ProductDetailsLayoutContext';
import { ProductDetailsVariantContextProvider } from '../context/ProductDetailsVariantContext';

import { LookbookNavigationArrows } from './LookbookNavigationArrows';
import { LookbookProductDetailsModalContent } from './LookbookProductDetailsModalContent';
import { LookbookProductDetailsModalLoader } from './LookbookProductDetailsModalLoader';
import { LookbookProductNotFoundContent } from './LookbookProductNotFoundContent';

interface Props {
  hideArrows?: boolean;
}

export const LookbookProductDetailsModal = ({ hideArrows = false }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = React.useState(false);
  const { selectedPricelist } = useLookbookContext();
  const { lookbookId, productId, variantId } = useLookbookDetailsModalContext();
  const { data: productDetails, isLoading, isError } = useLookbookProduct(productId, lookbookId, selectedPricelist?.id);
  const { trackEvent } = useAmplitude();
  const { trackGTMEvent } = useGTM();

  const hasProductId = isDefined(productId) && !isEmpty(productId);
  const params = useParams<{ lookbookId: Id }>();

  useBodyLock(hasProductId);

  const hideModal = () => {
    setIsOpen(false);
  };

  const removeInfoFromUrl = React.useCallback(() => {
    navigate(clearLookbookProductSearchQuery(location.search));
  }, [navigate, location.search]);

  const handleGetProductUrl = React.useCallback((urlParams: ProductUrlProps) => getUrlForLookbookProduct(urlParams), [params.lookbookId]);

  React.useEffect(() => {
    if (isDefined(productId)) {
      trackEvent({ name: 'lookbookPage.product.opened' });
    }
  }, [productId, trackEvent]);

  React.useEffect(() => {
    setIsOpen(hasProductId);
  }, [hasProductId]);

  React.useEffect(() => {
    if (!isDefined(productDetails)) {
      return;
    }
    trackGTMEvent('view_item', {
      ecommerce: {
        currency: selectedPricelist?.currency.currency,
        items: mapProductsToGAItems([productDetails], {
          item_list_id: GAProductListItemId.Lookbook,
          item_list_name: GAProductListItemName.Lookbook,
        }),
        value: productDetails.rrpAsNumber,
      },
    });
  }, [productDetails, trackGTMEvent, selectedPricelist, lookbookId]);

  return (
    <Portal>
      <CSSTransition
        in={isOpen}
        timeout={150}
        classNames={{
          enter: 'opacity-0 scale-90',
          enterActive: '!opacity-100 !scale-100 transition-[opacity,transform]',
          exit: 'opacity-100 scale-100',
          exitActive: '!opacity-0 !scale-90 transition-[opacity,transform]',
        }}
        onExited={removeInfoFromUrl}
        unmountOnExit
      >
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div className="fixed inset-0 z-pdm h-full w-full bg-neutral-96" onKeyDown={handleEscapeKey(hideModal)}>
          <FocusTrap>
            <ProductDetailsLayoutContextProvider>
              {isLoading ?
                <LookbookProductDetailsModalLoader onHideModal={hideModal} />
              : isError ?
                <LookbookProductNotFoundContent onHideModal={hideModal} />
              : isDefined(productDetails) ?
                <ProductDetailsVariantContextProvider
                  productId={productId}
                  variantId={variantId}
                  productDetails={productDetails}
                  getProductUrl={handleGetProductUrl}
                >
                  <LookbookProductDetailsModalContent onHideModal={hideModal} hideArrows={hideArrows} />
                </ProductDetailsVariantContextProvider>
              : <LookbookProductNotFoundContent onHideModal={hideModal} />}
            </ProductDetailsLayoutContextProvider>
            <div className="absolute left-0 right-0 top-0 flex justify-between gap-5 p-4 sm:hidden">
              <div>{!hideArrows && <LookbookNavigationArrows />}</div>
              <Button icon={CancelSmall} aria-label="Close modal" color="secondary" variant="flat" className="z-pdm" onClick={hideModal} />
            </div>
          </FocusTrap>
        </div>
      </CSSTransition>
    </Portal>
  );
};
